import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { usePublicSiteData } from "./PublicSiteContext";
import { WebsiteMemberDto } from "../../models/WebsiteModels";
import PublicSectionTitle from "./PublicSectionTitle";
import PublicMemberModal from "./PublicMemberModal";
import { StageImage, useStage } from "./StagedImageLoading";

const PublicMembers = () => {
  const website = usePublicSiteData();
  const members = website?.members ?? [];
  const memberImageCount = members.filter((m) => m.profilePictureUrl).length;
  useStage("members", memberImageCount);
  const [selected, setSelected] = useState<WebsiteMemberDto | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(1);
  const [step, setStep] = useState(352); // 320 + 32 default
  const containerRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const count = members.length;
  const maxIndex = Math.max(0, count - visibleCount);
  const canNavigate = count > visibleCount;

  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const firstCard = track?.querySelector(".psite-doctor") as HTMLElement | null;
    if (!viewport || !firstCard) return;

    const cardWidth = firstCard.getBoundingClientRect().width;
    const styles = window.getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || "32") || 32;
    const nextStep = cardWidth + gap;
    setStep(nextStep);

    const fit = Math.max(1, Math.floor((viewport.clientWidth + gap) / nextStep));
    setVisibleCount(Math.min(fit, count || 1));
  }, [count]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure, members.length]);

  useEffect(() => {
    setCurrentIndex((i) => Math.min(i, maxIndex));
  }, [maxIndex]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxIndex));
  }, [maxIndex]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPrev();
      if (e.key === "ArrowRight") goToNext();
    };

    container.addEventListener("keydown", handleKeyDown);
    return () => container.removeEventListener("keydown", handleKeyDown);
  }, [goToNext, goToPrev]);

  const trackStyle = useMemo(
    () => ({
      transform: `translate3d(-${currentIndex * step}px, 0, 0)`,
    }),
    [currentIndex, step],
  );

  return (
    <section className="psite-section psite-section--tint">
      <div className="psite-section__inner">
        <PublicSectionTitle
          title="Our Members"
          description="Meet the members of our team."
        />
        {count > 0 ? (
          <div
            className="psite-members-carousel"
            ref={containerRef}
            tabIndex={0}
          >
            {canNavigate && (
              <button
                className="psite-carousel__arrow psite-carousel__arrow--prev"
                onClick={goToPrev}
                aria-label="Previous members"
                disabled={currentIndex === 0}
              >
                ‹
              </button>
            )}

            <div className="psite-doctors-viewport" ref={viewportRef}>
              <div
                className={`psite-doctors-track${
                  canNavigate ? "" : " psite-doctors-track--centered"
                }`}
                ref={trackRef}
                style={trackStyle}
              >
                {members.map((member, index) => (
                  <MemberCard
                    key={`${member.userName}-${index}`}
                    member={member}
                    onDetails={() => setSelected(member)}
                  />
                ))}
              </div>
            </div>

            {canNavigate && (
              <button
                className="psite-carousel__arrow psite-carousel__arrow--next"
                onClick={goToNext}
                aria-label="Next members"
                disabled={currentIndex >= maxIndex}
              >
                ›
              </button>
            )}
          </div>
        ) : (
          <p className="psite-section__empty">
            Member profiles will appear here once published.
          </p>
        )}
      </div>

      {selected && (
        <PublicMemberModal
          member={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </section>
  );
};

interface MemberCardProps {
  member: WebsiteMemberDto;
  onDetails: () => void;
}

const MemberCard = ({ member, onDetails }: MemberCardProps) => (
  <div className="psite-doctor">
    <div className="psite-doctor__media">
      {member.profilePictureUrl ? (
        <StageImage
          stage="members"
          src={member.profilePictureUrl}
          alt={member.userName}
        />
      ) : (
        <span>{member.userName.charAt(0).toUpperCase()}</span>
      )}
    </div>
    <div className="psite-doctor__body">
      <h3>{member.userName}</h3>
      <p className="psite-doctor__title">
        {member.designation || member.roleName || "Member"}
      </p>
      {member.qualification && (
        <p className="psite-doctor__qualification">{member.qualification}</p>
      )}
      {member.bio && <p className="psite-doctor__bio">{member.bio}</p>}
      <button
        className="psite-btn psite-btn--primary psite-doctor__btn"
        onClick={onDetails}
      >
        Details
      </button>
    </div>
  </div>
);

export default PublicMembers;
